import {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  ConnectionMode,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
} from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import "@xyflow/react/dist/style.css";
import "./workflow.css";
import { Button, Icon, Input } from "@elaniintc/goes-design-system";
import { useDebounceValue } from "usehooks-ts";
import { useWorkflowStore } from "./store/workflowStore";
import { WorkflowNode } from "./components/node";
import { WorkflowEdge } from "./components/edge";
// import { useAutoLayout } from "./hooks/useAutoLayout";
import { useUIStore } from "./store/uiStore";
import NodeSelectionModal from "./modals/node-selection";
import {
  useMasiveDelete,
  useUpdateNode,
  useUpdateNodesPositions,
} from "./services/nodes.api";
import type { UpdateNodeBody } from "./services/nodes.api";
import { NodeTypes, type NodeData, type RefName } from "./types/workflow";
import usePositionSync from "./hooks/usePositionSync";
import NodeDetailsModal from "./modals/node-details";
import NodeValidationErrorsModal from "./modals/node-validation-errors";
import NodeValidationSuccessModal from "./modals/node-validation-success";
import NodeErrorsSummaryModal from "./modals/node-error-summary";
import NodeTestInputModal from "./modals/node-test-input";
import NodeSearch from "./modals/node-search";
import { ConnectionManager } from "@/workflow/components/configurations/merge/connection-manager";
import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";

const nodeTypes = {
  "worflow-node": WorkflowNode,
};

const edgeTypes = {
  "workflow-edge": WorkflowEdge,
};

function WorkflowCanvasInner() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  // const { layoutHorizontal } = useAutoLayout();
  const { screenToFlowPosition, zoomIn, zoomOut, getZoom, zoomTo, fitView } =
    useReactFlow();

  const {
    nodes,
    edges,
    flowId,
    getNodeById,
    setNodeConnecting,
    onNodesChange,
    onEdgesChange,
    setSelectedNodes,
    setSelectedEdges,
    clearSelection,
    setLastClickPosition,
  } = useWorkflowStore();
  const {
    setNodeCreatorOpen,
    modalState,
    clearErrors,
    isFlowBlocked,
    incrementChangeCounter,
  } = useUIStore();

  const onPaneClick = useCallback(
    (event: React.MouseEvent) => {
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!bounds) return;

      const position = screenToFlowPosition({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      setLastClickPosition(position);
      clearSelection();
    },
    [screenToFlowPosition, setLastClickPosition, clearSelection],
  );

  const [zoom, setZoom] = useState(getZoom());
  const handleZoomIn = useCallback(() => {
    zoomIn();
    setTimeout(() => {
      setZoom(getZoom() * 100); // Update zoom state after zooming in
    }, 0);
  }, [zoomIn, getZoom]);

  const handleZoomOut = useCallback(() => {
    zoomOut();
    setTimeout(() => {
      setZoom(getZoom() * 100); // Update zoom state after zooming out
    }, 0);
  }, [zoomOut, getZoom]);

  const handleResetZoom = useCallback(() => {
    fitView();
    setZoom(200); // Reset zoom state
  }, [setZoom, fitView]);

  const [debouncedZoom] = useDebounceValue(zoom, 300);

  useEffect(() => {
    if (debouncedZoom) {
      zoomTo(debouncedZoom / 100);
    }
  }, [debouncedZoom, zoomTo]);

  useEffect(() => {
    ConnectionManager.recalculateAllVisibility();
  }, [nodes, edges]);

  const onSelectionChange = useCallback(
    ({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) => {
      setSelectedEdges(edges.map((edge) => edge.id));
      setSelectedNodes(
        nodes
          .filter(({ data }) => {
            return data.type !== "initializer";
          })
          .map((node) => node.id),
      );
    },
    [setSelectedEdges, setSelectedNodes],
  );

  const onInit = useCallback(() => {
    fitView({
      maxZoom: 1,
    });
  }, [fitView]);

  const { mutateAsync: deleteNodes } = useMasiveDelete(flowId);

  const onMasiveDelete = async (nodes: Node[]) => {
    if (isFlowBlocked) return; // No permitir eliminación masiva cuando está bloqueado
    await deleteNodes({
      nodes: nodes
        .filter((n) => {
          return n.data.type !== NodeTypes.INITIALIZER;
        })
        .map((node) => node.id),
    });
    incrementChangeCounter();
  };

  const { mutateAsync: updateNodesPositions } = useUpdateNodesPositions();

  const triggerPositionSync = usePositionSync(updateNodesPositions);

  const onNodeChangesSync = useCallback(
    (nc: NodeChange[]) => {
      const nodesToUpdate: {
        id: string;
        position: { x: number; y: number };
      }[] = [];
      nc.filter((n) => n.type === "position" && !n.dragging).forEach((n) => {
        if (n.type !== "position" || !n.position) return;
        nodesToUpdate.push({
          id: n.id,
          position: {
            x: n.position?.x,
            y: n.position?.y,
          },
        });
      });
      if (nodesToUpdate.length === 0) return;
      clearErrors();
      triggerPositionSync(nodesToUpdate);
      incrementChangeCounter();
    },
    [triggerPositionSync, clearErrors, incrementChangeCounter],
  );

  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  // Validar si una conexión es válida antes de permitirla
  const isValidConnection = useCallback(
    (connection: Connection | Edge): boolean => {
      // Extraer source, sourceHandle y target de Connection o Edge
      const source =
        "source" in connection
          ? connection.source
          : (connection as Edge).source;
      const sourceHandle =
        "sourceHandle" in connection
          ? (connection.sourceHandle ?? null)
          : ((connection as Edge).sourceHandle ?? null);
      const target =
        "target" in connection
          ? connection.target
          : (connection as Edge).target;

      if (!source || !sourceHandle || !target) {
        return false;
      }

      // Verificar si esta salida ya tiene una conexión existente en los edges
      const existingConnection = edges.find(
        (edge) => edge.source === source && edge.sourceHandle === sourceHandle,
      );

      // Si ya existe una conexión desde esta salida, no permitir otra
      if (existingConnection) {
        return false;
      }

      // Para nodos Wait y Merge, también verificar las conexiones en los inputs
      const targetNode = getNodeById(target);
      if (targetNode) {
        const targetData = targetNode.data as unknown as NodeData;
        if (
          (targetData.type === NodeTypes.WAIT ||
            targetData.type === NodeTypes.MERGE) &&
          Array.isArray(targetData.inputs)
        ) {
          const inputTypeFilter =
            targetData.type === NodeTypes.MERGE ? "MERGE_ENTRY" : "THREAD";

          const hasConnectionInInputs = targetData.inputs.some((input) => {
            if (input.type !== inputTypeFilter) return false;
            const connections = input.source_connections || [];
            return connections.some(
              (conn) =>
                conn?.source_node_id === source &&
                (conn?.field?.id === sourceHandle ||
                  conn?.source_node_output_id === sourceHandle),
            );
          });

          if (hasConnectionInInputs) {
            return false;
          }
        }
      }

      // Verificar también en todos los nodos si alguna salida ya está conectada
      // Esto previene conexiones duplicadas desde cualquier nodo
      for (const node of nodes) {
        const nodeData = node.data as unknown as NodeData;
        if (
          (nodeData.type === NodeTypes.WAIT ||
            nodeData.type === NodeTypes.MERGE) &&
          Array.isArray(nodeData.inputs)
        ) {
          const inputTypeFilter =
            nodeData.type === NodeTypes.MERGE ? "MERGE_ENTRY" : "THREAD";
          const hasConnection = nodeData.inputs.some((input) => {
            if (input.type !== inputTypeFilter) return false;
            const connections = input.source_connections || [];
            return connections.some(
              (conn) =>
                conn?.source_node_id === source &&
                (conn?.field?.id === sourceHandle ||
                  conn?.source_node_output_id === sourceHandle),
            );
          });
          if (hasConnection) {
            return false;
          }
        }
      }

      return true;
    },
    [edges, getNodeById, nodes],
  );

  const onEdgesDelete = async (deletedEdges: Edge[]) => {
    if (isFlowBlocked) return;

    for (const edge of deletedEdges) {
      const targetNode = getNodeById(edge.target);
      if (!targetNode) continue;

      const data = targetNode.data as unknown as NodeData;

      if (
        (data.type === NodeTypes.WAIT || data.type === NodeTypes.MERGE) &&
        Array.isArray(data.inputs)
      ) {
        const handleMatch =
          edge.targetHandle?.match(/^input-(\d+)$/) ||
          edge.targetHandle?.match(/^merge-input-(\d+)$/);
        if (handleMatch) {
          const updatedInputs = data.inputs.map((input) => {
            if (!input.source_connections) return input;
            return {
              ...input,
              source_connections: input.source_connections.filter(
                (conn) =>
                  !(
                    conn.source_node_id === edge.source &&
                    (conn.field?.id === edge.sourceHandle ||
                      conn.source_node_output_id === edge.sourceHandle)
                  ),
              ),
            };
          });

          const updatedEdgesArr = (data.edges || []).filter(
            (e) =>
              !(
                e.source === edge.source &&
                e.target_handler === edge.targetHandle
              ),
          );

          await updateNode({
            node_id: edge.target,
            node: {
              ...data,
              flow_id: flowId,
              ref_name: data.type as RefName,
              display_name: data.displayName,
              order: data.order,
              position_x: targetNode.position.x,
              position_y: targetNode.position.y,
              type: data.node_type,
              inputs: updatedInputs,
              edges: updatedEdgesArr.length > 0 ? updatedEdgesArr : undefined,
            } as unknown as UpdateNodeBody,
          });
          incrementChangeCounter();
          continue;
        }
      }

      await updateNode({
        node_id: edge.target,
        node: {
          flow_id: flowId,
          ref_name: data.type as RefName,
          display_name: data.displayName,
          order: data.order,
          position_x: targetNode.position.x,
          position_y: targetNode.position.y,
          type: data.node_type,
          edge: null, // Clear the incoming edge
        },
      });
      incrementChangeCounter();
    }
  };

  const onNodeConnection = (e: Connection) => {
    // Validar la conexión antes de procesarla
    if (!isValidConnection(e)) {
      return;
    }
    if (!e.target || !e.source || !e.sourceHandle || !e.targetHandle) return;

    // Limpiar errores existentes
    clearErrors();

    const node = getNodeById(e.target);
    if (!node) return;
    const data = node.data as unknown as NodeData;

    if (
      (data.type === NodeTypes.WAIT || data.type === NodeTypes.MERGE) &&
      Array.isArray(data.inputs)
    ) {
      const handleMatch = e.targetHandle.match(/^(?:input|merge-input)-(\d+)$/);
      if (!handleMatch) return;

      const inputIndex = parseInt(handleMatch[1], 10) - 1;

      const isMerge = data.type === NodeTypes.MERGE;
      const inputTypeFilter = isMerge ? "MERGE_ENTRY" : "THREAD";
      const targetInputs = data.inputs.filter(
        (i) => i.type === inputTypeFilter,
      );

      if (inputIndex < 0 || inputIndex >= targetInputs.length) return;

      // Crear copia profunda de los inputs
      const updatedInputs = data.inputs.map((input) => ({
        ...input,
        source_connections: input.source_connections
          ? [...input.source_connections]
          : [],
      }));

      let filteredCount = 0;
      let targetInputArrayIndex = -1;
      for (let i = 0; i < updatedInputs.length; i++) {
        if (updatedInputs[i].type === inputTypeFilter) {
          if (filteredCount === inputIndex) {
            targetInputArrayIndex = i;
            break;
          }
          filteredCount++;
        }
      }

      if (targetInputArrayIndex === -1) return;

      const targetInput = updatedInputs[targetInputArrayIndex];
      const existingConnections = targetInput.source_connections || [];

      const alreadyConnected = existingConnections.some(
        (conn) =>
          conn?.source_node_id === e.source &&
          (conn?.field?.id === e.sourceHandle ||
            conn?.source_node_output_id === e.sourceHandle),
      );

      if (!alreadyConnected) {
        existingConnections.push({
          field: {
            id: e.sourceHandle,
            name: e.sourceHandle,
            type: isMerge ? "merge" : "thread",
          },
          source_node_id: e.source,
        });
      }

      updatedInputs[targetInputArrayIndex] = {
        ...targetInput,
        connection: targetInput.connection || e.source,
        source_connections: existingConnections,
      };

      const newEdge: Edge = {
        id: `${e.source}-${e.target}-${e.targetHandle}-new`,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        type: "workflow-edge" as const,
        data: {
          source: { id: e.source, handle: e.sourceHandle },
          target: { id: e.target, handle: e.targetHandle },
        },
      };

      useWorkflowStore.setState((state) => ({
        edges: [...state.edges, newEdge],
      }));

      const existingEdgesMap = new Map<string, string>();
      if (data.edges) {
        data.edges.forEach((edge) => {
          if (edge.target_handler && edge.source_handler && edge.source) {
            existingEdgesMap.set(
              `${edge.source}-${edge.target_handler}`,
              edge.source_handler,
            );
          }
        });
      }

      const filteredInputsOnly = updatedInputs.filter(
        (input) => input.type === inputTypeFilter,
      );
      const apiEdges = filteredInputsOnly.flatMap((input, idx) => {
        if (!Array.isArray(input.source_connections)) return [];
        const targetHandler = isMerge
          ? `merge-input-${idx + 1}`
          : `input-${idx + 1}`;
        return input.source_connections.map((conn) => ({
          source: conn.source_node_id,
          source_handler:
            conn.field?.id ||
            conn.source_node_output_id ||
            existingEdgesMap.get(`${conn.source_node_id}-${targetHandler}`) ||
            "source",
          target_handler: targetHandler,
          target_input_name: input.name,
        }));
      });

      updateNode({
        node_id: e.target,
        node: {
          flow_id: flowId,
          ref_name: data.type as RefName,
          display_name: data.displayName,
          order: data.order,
          position_x: node.position.x,
          position_y: node.position.y,
          type: data.node_type,
          edges: apiEdges.length > 0 ? apiEdges : undefined,
          inputs: updatedInputs,
        },
      }).then(() => {
        incrementChangeCounter();
      });
      return;
    }

    const newEdge: Edge = {
      id: `${e.source}-${e.target}-${e.targetHandle}-new`,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      type: "workflow-edge" as const,
      data: {
        source: { id: e.source, handle: e.sourceHandle },
        target: { id: e.target, handle: e.targetHandle },
      },
    };

    useWorkflowStore.setState((state) => ({
      edges: [...state.edges, newEdge],
    }));

    // Conexión normal para otros tipos de nodos
    updateNode({
      node_id: e.target,
      node: {
        flow_id: flowId,
        ref_name: data.type as RefName,
        display_name: data.displayName,
        order: data.order,
        position_x: node.position.x,
        position_y: node.position.y,
        type: data.node_type,
        edge: {
          source: e.source,
          source_handler: e.sourceHandle,
          target_handler: e.targetHandle,
        },
      },
    }).then(() => {
      incrementChangeCounter();
    });
  };

  // const onOrderNodes = async () => {
  //   const nodes = await layoutHorizontal();
  //   const newNodePosition =
  //     nodes?.nodes.map((n) => {
  //       return {
  //         id: n.id,
  //         position: {
  //           x: n.position.x,
  //           y: n.position.y,
  //         },
  //       };
  //     }) || [];
  //   if (newNodePosition.length === 0) return;
  //   triggerPositionSync(newNodePosition);
  // };

  return (
    <div
      className="bg-neutral-object -mx-4 -my-2 tablet:-mx-4 laptop:-mx-6 desktop:-mx-8 w-[calc(100%+32px)] tablet:w-[calc(100%+32px)] laptop:w-[calc(100%+48px)] desktop:w-[calc(100%+64px)] h-[calc(100%+16px)]"
      ref={reactFlowWrapper}
    >
      {modalState === "create" && <NodeSelectionModal />}
      {modalState === "details" && <NodeDetailsModal />}
      {modalState === "error-summary" && <NodeErrorsSummaryModal />}
      {modalState === "validation-success" && <NodeValidationSuccessModal />}
      {modalState === "error" && <NodeValidationErrorsModal />}
      {modalState === "test-input" && <NodeTestInputModal />}
      {modalState === "search-nodes" && <NodeSearch />}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onlyRenderVisibleElements
        onNodesChange={
          isFlowBlocked
            ? undefined
            : (nc) => {
                if (nc.length === 0) return;
                onNodeChangesSync(nc);
                onNodesChange(nc);
              }
        }
        onEdgesChange={isFlowBlocked ? undefined : onEdgesChange}
        onConnect={isFlowBlocked ? undefined : onNodeConnection}
        isValidConnection={isFlowBlocked ? () => false : isValidConnection}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onPaneClick={onPaneClick}
        zoomOnDoubleClick={!isFlowBlocked}
        zoomOnPinch={false}
        snapToGrid={true}
        snapGrid={[22, 22]}
        onInit={onInit}
        multiSelectionKeyCode="Shift"
        selectionOnDrag={!isFlowBlocked}
        selectionMode={SelectionMode.Full}
        connectionMode={ConnectionMode.Strict}
        noDragClassName="no-drag"
        nodesDraggable={!isFlowBlocked}
        nodesConnectable={!isFlowBlocked}
        connectionLineStyle={{
          stroke: "var(--border-neutral)",
          strokeWidth: 2,
        }}
        onConnectStart={
          isFlowBlocked
            ? undefined
            : (_, { nodeId }) => {
                setNodeConnecting(nodeId);
              }
        }
        onConnectEnd={
          isFlowBlocked
            ? undefined
            : () => {
                clearErrors();
                setNodeConnecting(null);
              }
        }
        onSelectionChange={isFlowBlocked ? undefined : onSelectionChange}
        onViewportChange={({ zoom }) => {
          setZoom(zoom * 100);
        }}
        onDelete={
          isFlowBlocked
            ? undefined
            : ({ nodes, edges }) => {
                if (nodes.length > 0) onMasiveDelete(nodes);
                if (edges.length > 0) onEdgesDelete(edges);
              }
        }
        connectionLineType={ConnectionLineType.SmoothStep}
        attributionPosition="top-right"
        proOptions={{
          hideAttribution: true,
        }}
      >
        <Background
          id={"background-1"}
          variant={BackgroundVariant.Dots}
          gap={[22, 22]}
          size={3}
          className=""
          color="var(--neutral-object-accent)"
        />
        {!isFlowBlocked && (
          <div className="absolute top-4 right-8 z-20 bg-neutral-object">
            <Button
              variant="quiet"
              type="button"
              size="icon"
              aria-label="Agregar nodo"
              onClick={() => {
                setNodeCreatorOpen(true);
              }}
            >
              <Icon type="far" name="plus" />
            </Button>
          </div>
        )}
        <div className="absolute bottom-3 left-8 flex items-center gap-2 z-20">
          <div className="bg-neutral-object flex items-center rounded-lg">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              group="left"
              aria-label="zoom out"
              onClick={handleZoomOut}
            >
              <Icon type="far" name="search-minus" />
            </Button>
            <div className="relative">
              <Input
                value={zoom.toFixed(0)}
                onChange={(e) => {
                  // admit just digits
                  const value = e.target.value.replace(/\D/g, "");
                  setZoom(Number(value) || 0);
                }}
                group="middle-up"
                maxLength={3}
                variant="xs"
                className="w-16 font-geist-mono pl-4 text-body-quiet"
              />
              <span className="absolute right-0 top-1.5 flex items-center pr-2 text-sm text-body-quiet">
                %
              </span>
            </div>
            <Button
              type="button"
              variant="quiet"
              size="icon"
              aria-label="zoom in"
              group="right"
              onClick={handleZoomIn}
            >
              <Icon type="far" name="search-plus" />
            </Button>
          </div>
          <div className="bg-neutral-object flex items-center rounded-lg">
            <OptionTooltip title="Ajustar vista">
              <Button
                size="icon"
                variant="quiet"
                onClick={handleResetZoom}
                aria-label="reset zoom"
              >
                <Icon type="far" name="expand" />
              </Button>
            </OptionTooltip>
          </div>
          {/* <div className="bg-neutral-object flex items-center rounded-lg">
            <Button
              size="icon"
              variant="quiet"
              onClick={onOrderNodes}
              aria-label="reset zoom"
            >
              <Icon type="far" name="diagram-nested" />
            </Button>
          </div> */}
        </div>
        {/* <div className="absolute bottom-3 right-8 flex items-center z-20">
          <Button
            type="button"
            variant="quiet"
            size="icon"
            aria-label="undo last action"
            group="left"
            onClick={undo}
            disabled={!canUndo()}
          >
            <Icon type="far" name="undo" />
          </Button>
          <Button
            type="button"
            variant="quiet"
            size="icon"
            aria-label="redo last action"
            group="right"
            onClick={redo}
            disabled={!canRedo()}
          >
            <Icon type="far" name="redo" />
          </Button>
        </div> */}
      </ReactFlow>
    </div>
  );
}

export function WorkflowCanvas() {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner />
    </ReactFlowProvider>
  );
}
